import type { Subscription, SubscriptionLine, LineAmendment, LineFreeze } from '@/types'
import { getDayName, toLocalDateString } from '@/utils/deliveryDays'
import { formatDate as formatFullDate } from '@/utils/dateFormat'
import {
  nextDeliveryDateFor,
  lastDeliveryDateFor,
  neverDelivers,
  toStartOfDay,
  startDateIfFuture,
  changeFromTheStart,
  standingQuantityFor,
} from '@/utils/deliverySchedule'

/**
 * The standing weekly order, and the scheduled changes sitting on it, read out
 * of the subscription lines and amendments.
 *
 * Extracted from WeeklyOrderPanel so the edit dialog can show the same
 * sentences. Written twice they would drift, and a card saying one thing while
 * the dialog it opens says another is exactly the bug this is meant to avoid.
 */

/** "A", "A and B", "A, B and C". */
const listOut = (parts: string[]): string => parts.length < 2
  ? (parts[0] ?? '')
  : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`

/** Sentence case for a fragment used at the start of an annotation. */
const capitalise = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1)

/** Shift a date by whole weeks; deliveries repeat weekly. */
const addWeeks = (date: Date, weeks: number): Date => {
  const shifted = new Date(date)
  shifted.setDate(shifted.getDate() + weeks * 7)
  return shifted
}

/**
 * A pause read back out of the data: one line ended, and another for the same
 * day scheduled to start after the gap. CRM has no "paused" state, so this is
 * the only way to tell a pause from a plain removal followed by a new order.
 */
const detectPause = (lines: SubscriptionLine[]): {
  ending: SubscriptionLine
  resuming: SubscriptionLine
  lastBefore: Date
  firstAfter: Date
} | null => {
  for (const ending of lines) {
    const lastBefore = lastDeliveryDateFor(ending)
    // The line that stops must already be running; one that both starts and
    // ends in the future is a bounded addition, not the front half of a pause.
    if (!lastBefore || startDateIfFuture(ending)) continue

    for (const resuming of lines) {
      if (resuming.id === ending.id) continue
      const firstAfter = startDateIfFuture(resuming)
      if (firstAfter && firstAfter > lastBefore) {
        return { ending, resuming, lastBefore, firstAfter }
      }
    }
  }
  return null
}

/** A week of this product at what it will actually cost. */
export const rowTotalFor = (row: OrderRow): number =>
  Array.from(row.linesByDay.entries())
    .filter(([, line]) => isLive(line))
    .reduce((sum, [day]) => sum + (row.quantityByDay.get(day) ?? 0) * (row.unitPriceByDay.get(day) ?? row.unitPrice), 0)

/** A line still delivering is "live"; one that has run out is not. */
export const isLive = (line: SubscriptionLine): boolean => nextDeliveryDateFor(line) !== null

/**
 * How many weekly deliveries an amendment covers, inclusive of both ends.
 * effective_to is the last day the change applies, so a same-day range is one
 * delivery, not zero.
 */
const weeksBetween = (from: string, to?: string | null): number | null => {
  if (!to) return null
  const days = Math.round(
    (toStartOfDay(to).getTime() - toStartOfDay(from).getTime()) / 86400000
  )
  return Math.max(1, Math.floor(days / 7) + 1)
}

/** A product in the standing order, with the line it holds on each day. */
export interface OrderRow {
  productId: string
  name: string
  image: string | null
  /** A representative single price for the product (the soonest-delivering
   *  line's), for places that show one figure, e.g. the product detail popup. */
  unitPrice: number
  /** The price on each day, taken from that day's own governing line. A product
   *  can cost different amounts on different days when an old line and a new
   *  (uplifted) line sit on different days, so each day's card and the row total
   *  price that day's line, not one price for the whole product (DF-242). */
  unitPriceByDay: Map<number, number>
  /** The line that governs each day now: the one delivering soonest. Cells and
   *  totals use this, so a paused day is never counted twice. */
  linesByDay: Map<number, SubscriptionLine>
  /** Every line on the day. A pause leaves two: the one it ended and the one
   *  it scheduled to follow, and the annotation needs both to say so. */
  allLinesByDay: Map<number, SubscriptionLine[]>
  /** What the customer actually gets on each day, which on a line that hasn't
   *  started is its opening change rather than the line's stored quantity. */
  quantityByDay: Map<number, number>
}

/** A scheduled deviation from the standing order, described in plain words. */
export interface ScheduledChange {
  productId: string
  day: number
  text: string
  /**
   * Which kind of change, because undoing each one means a different call:
   * an amendment is deleted, an ending has its end date cleared, and a line
   * that hasn't started is removed outright.
   */
  kind: 'amendment' | 'ending' | 'starting' | 'pause' | 'freeze'
  lineId: string
  amendmentId?: string
  /** For a freeze (DF-350): every Line Freeze the pause covers, one per day. */
  freezeIds?: string[]
  /** Every line this change covers, when it merges several into one sentence. */
  lineIds?: string[]
  /** For a pause: each ended line paired with the line scheduled to follow it. */
  pausedLines?: { lineId: string, resumeLineId: string }[]
}

export function relevantLines(subscriptions: Subscription[]): SubscriptionLine[] {
    const today = toStartOfDay(new Date())
    return subscriptions
      .filter(sub => sub.statecode !== 1)
      .flatMap(sub => sub.lines)
      .filter(line => {
        if (line.statecode === 1) return false
        // The delivery surcharge is a product line but is not something the
        // customer manages, so it never appears in the order/management views.
        // It is surfaced under the weekly total instead (DF-88).
        if (line.is_surcharge || line.is_one_off) return false
        // A cancelled-before-it-started line keeps a future end date, so it
        // would otherwise slip through the "still worth mentioning" test below
        // and render as a range running backwards.
        if (neverDelivers(line)) return false
        if (isLive(line)) return true
        return !!line.end_date && toStartOfDay(line.end_date) >= today
      })
  }

export function deliveryDays(relevant: SubscriptionLine[]): number[] {
    const days = new Set<number>()
    relevant.filter(isLive).forEach(line => {
      if (line.delivery_day !== null && line.delivery_day !== undefined) days.add(line.delivery_day)
    })
    // Monday-first ordering; Sunday (0) sits at the end of the week.
    return Array.from(days).sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
  }

export function rows(relevant: SubscriptionLine[], amendments: LineAmendment[]): OrderRow[] {
    const byProduct = new Map<string, OrderRow>()
    for (const line of relevant) {
      if (!line.product_id) continue
      let row = byProduct.get(line.product_id)
      if (!row) {
        row = {
          productId: line.product_id,
          name: line.product_name || line.name || 'Product',
          image: line.product_image ?? null,
          unitPrice: line.unit_price_inc_vat ?? line.unit_price ?? 0,
          unitPriceByDay: new Map(),
          linesByDay: new Map(),
          allLinesByDay: new Map(),
          quantityByDay: new Map(),
        }
        byProduct.set(line.product_id, row)
      }
      if (line.delivery_day !== null && line.delivery_day !== undefined) {
        const forDay = row.allLinesByDay.get(line.delivery_day) ?? []
        forDay.push(line)
        row.allLinesByDay.set(line.delivery_day, forDay)
      }
    }

    // Pick the one line that speaks for each day. A pause leaves two lines on
    // the same day and both can be live at once, so taking them all would show
    // and charge for the product twice.
    for (const row of byProduct.values()) {
      for (const [day, lines] of row.allLinesByDay) {
        const governing = [...lines].sort((a, b) => {
          const nextA = nextDeliveryDateFor(a)
          const nextB = nextDeliveryDateFor(b)
          if (nextA && nextB) return nextA.getTime() - nextB.getTime()
          if (nextA) return -1
          if (nextB) return 1
          return 0
        })[0]
        if (governing) {
          row.linesByDay.set(day, governing)
          row.quantityByDay.set(day, standingQuantityFor(governing, amendments))
          row.unitPriceByDay.set(day, governing.unit_price_inc_vat ?? governing.unit_price ?? 0)
        }
      }
    }

    // A representative single price for the whole product, the soonest-
    // delivering line's, for the few places that still show one figure (the
    // product detail popup). The day-by-day cards and the row total price each
    // day from its own line via unitPriceByDay: a product can cost different
    // amounts on different days when an old line and a new (uplifted) line sit
    // on different days, and showing one price on every day made Delivery
    // Management disagree with My Deliveries, which prices per line (DF-229, and
    // now per day for DF-242).
    for (const row of byProduct.values()) {
      const soonest = Array.from(row.linesByDay.values())
        .map(line => ({ line, next: nextDeliveryDateFor(line) }))
        .filter((x): x is { line: SubscriptionLine, next: Date } => x.next !== null)
        .sort((a, b) => a.next.getTime() - b.next.getTime())[0]?.line
      if (soonest) {
        row.unitPrice = soonest.unit_price_inc_vat ?? soonest.unit_price ?? row.unitPrice
      }
    }

    // A product earns its place by still delivering something; one that has
    // wholly finished drops off rather than lingering as an empty row.
    //
    // Alphabetical, except that a product which hasn't delivered yet sits at
    // the top until it does: something just added would otherwise land in the
    // middle of the list, where the customer has to hunt for what they only
    // just did. A product already delivering stays put when a day is added to
    // it, so the list only reorders for genuinely new products.
    const notStartedYet = (row: OrderRow): boolean => {
      const lines = Array.from(row.allLinesByDay.values()).flat().filter(isLive)
      return lines.length > 0 && lines.every(line => startDateIfFuture(line) !== null)
    }

    return Array.from(byProduct.values())
      .filter(row => Array.from(row.allLinesByDay.values()).flat().some(isLive))
      .sort((a, b) => {
        const aNew = notStartedYet(a)
        const bNew = notStartedYet(b)
        if (aNew !== bNew) return aNew ? -1 : 1
        return a.name.localeCompare(b.name)
      })
  }

/** The first date on or after `date` that falls on `weekday` (0 = Sunday). */
function onOrAfterWeekday(date: Date, weekday: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + ((weekday - d.getDay() + 7) % 7))
  return d
}

/** The last date on or before `date` that falls on `weekday` (0 = Sunday). */
function onOrBeforeWeekday(date: Date, weekday: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() - ((d.getDay() - weekday + 7) % 7))
  return d
}

export function changesByProduct(rows: OrderRow[], amendments: LineAmendment[], freezes: LineFreeze[] = []): Map<string, ScheduledChange[]> {
    const map = new Map<string, ScheduledChange[]>()

    const add = (change: ScheduledChange) => {
      const list = map.get(change.productId) ?? []
      list.push(change)
      map.set(change.productId, list)
    }

    for (const row of rows) {
      // A pause is taken on the product, not on one day, so its per-day halves
      // are gathered here and reported as a single change below.
      const pauses: { day: number, ending: string, resuming: string, firstMissed: Date, lastMissed: Date }[] = []
      const starting: { day: number, lineId: string, first: Date, last: Date | null, quantity: number }[] = []
      // Amendments already spoken for by the sentence about the product
      // starting, so they aren't also announced as a separate change.
      const folded = new Set<string>()

      for (const [day, lines] of row.allLinesByDay) {

        // A pause is not stored as such: it ends one line and schedules another
        // to follow. Read together they are one change, and describing them
        // separately ("last delivery 30th July" / "first delivery 3rd
        // September") leaves the customer to work out that it starts again.
        const paused = detectPause(lines)

        if (paused) {
          // Deliveries are weekly, so the skipped run is the week after the
          // last one that goes out to the week before it resumes.
          pauses.push({
            day,
            ending: paused.ending.id,
            resuming: paused.resuming.id,
            firstMissed: addWeeks(paused.lastBefore, 1),
            lastMissed: addWeeks(paused.firstAfter, -1),
          })
        }

        for (const line of lines) {
          const starts = paused ? null : startDateIfFuture(line)
          const lastDelivery = paused ? null : lastDeliveryDateFor(line)

          if (starts) {
            // A line that hasn't started yet still shows its quantity, so
            // without this it reads as part of the current order when the first
            // delivery could be weeks away. Collected rather than announced:
            // adding a product to two days makes two lines, and one sentence
            // below covers them all. A line with an end date as well is a
            // bounded run ("add for N weeks"), not a separate start and finish.
            // Saying "starts at 1" and "changing to 3 from the same date" in
            // two lines contradicts itself, so the opening change is folded in.
            const fromTheStart = changeFromTheStart(line, amendments)

            if (fromTheStart) folded.add(fromTheStart.amendment_id)

            starting.push({
              day,
              lineId: line.id,
              first: starts,
              last: lastDelivery,
              quantity: fromTheStart ? fromTheStart.new_quantity : line.quantity,
            })
          } else if (lastDelivery) {
            // A line with an end date is on its way out. Give the last delivery
            // date rather than the end boundary, which can fall on a day the
            // product isn't even delivered.
            const when = formatFullDate(lastDelivery)
            add({
              productId: row.productId,
              day,
              kind: 'ending',
              lineId: line.id,
              text: isLive(line)
                ? `Your last one is ${when}`
                : `Your last one was ${when}`,
            })
          }

          // Every pending change on the line, not just the first. CRM allows
          // several and does not stop them overlapping, so taking one left the
          // rest invisible and impossible to cancel from here. Soonest first,
          // so they read in the order they will happen.
          const pending = amendments
            .filter(a => a.subscription_line_id === line.id && !folded.has(a.amendment_id))
            .sort((a, b) => toStartOfDay(a.effective_from).getTime()
              - toStartOfDay(b.effective_from).getTime())

          // What the line normally carries, which the changes below deviate
          // from. Not line.quantity directly: a not-yet-started line's opening
          // change is its standing quantity.
          const standing = standingQuantityFor(line, amendments)

          for (const amendment of pending) {
            const startsOn = toStartOfDay(amendment.effective_from)
            const from = formatFullDate(startsOn)

            // A change can't outlive the line it sits on. An amendment stored
            // open-ended on a line that ends next week only affects the
            // deliveries up to that end, so describe what will actually happen
            // rather than what the amendment record says on its own.
            const lineEnd = lastDeliveryDateFor(line)
            const amendmentEnd = amendment.effective_to ? toStartOfDay(amendment.effective_to) : null
            const effectiveEnd = lineEnd && (!amendmentEnd || amendmentEnd > lineEnd)
              ? lineEnd
              : amendmentEnd

            const runFor = effectiveEnd
              ? weeksBetween(toLocalDateString(startsOn), toLocalDateString(effectiveEnd))
              : null

            // One verb, direction-aware, so the line reads as a sentence a
            // person would say ("Going up to 2 from Monday 14th") rather than
            // the old "Changing from 1 to 2 from Monday 14th", whose two
            // "from"s meant different things.
            const verb = amendment.new_quantity > standing ? 'Going up to' : 'Going down to'

            add({
              productId: row.productId,
              day,
              kind: 'amendment',
              lineId: line.id,
              amendmentId: amendment.amendment_id,
              text: amendment.new_quantity === 0
                ? `Paused from ${from}`
                : runFor === 1
                  // One delivery, so a date says it better than "for 1 week".
                  ? `${verb} ${amendment.new_quantity} on ${from} only`
                  : runFor
                    ? `${verb} ${amendment.new_quantity} for ${runFor} weeks from ${from}`
                    : `${verb} ${amendment.new_quantity} from ${from}`,
            })
          }
        }
      }

      if (starting.length > 0) {
        // Each day's line has its own first delivery, but the customer added
        // the product once and thinks of it as starting once. So: what they
        // get on each day, and the date the first of those lands.
        // One entry per day. Two lines for the product on the same day (on two
        // subscriptions, DF-386) would otherwise read "Mondays and Mondays";
        // the customer gets both, so their quantities add up.
        const perDay = new Map<number, number>()
        starting.forEach(x => perDay.set(x.day, (perDay.get(x.day) ?? 0) + x.quantity))
        const byDay = Array.from(perDay, ([day, quantity]) => ({ day, quantity }))
          .sort((a, b) => (a.day === 0 ? 7 : a.day) - (b.day === 0 ? 7 : b.day))
        const earliest = starting.reduce((a, b) => (b.first < a ? b.first : a), starting[0].first)

        const sameQuantity = byDay.every(x => x.quantity === byDay[0].quantity)
        const what = sameQuantity
          ? `${byDay[0].quantity} on ${listOut(byDay.map(x => `${getDayName(x.day)}s`))}`
          : listOut(byDay.map(x => `${x.quantity} on ${getDayName(x.day)}s`))

        const runs = starting.map(x => weeksBetween(
          toLocalDateString(x.first),
          x.last ? toLocalDateString(x.last) : null
        ))
        // A bounded run is only worth stating once, and only when every day
        // runs for the same number of weeks. Otherwise say when it starts and
        // leave the ends to the individual lines.
        const sharedRun = runs.every(r => r !== null && r === runs[0]) ? runs[0] : null

        add({
          productId: row.productId,
          day: starting[0].day,
          kind: 'starting',
          lineId: starting[0].lineId,
          lineIds: starting.map(x => x.lineId),
          text: sharedRun === 1
            ? `${capitalise(what)}, on ${formatFullDate(earliest)} only`
            : sharedRun
              ? `${capitalise(what)} for ${sharedRun} weeks, from ${formatFullDate(earliest)}`
              : `${capitalise(what)}, starting ${formatFullDate(earliest)}`,
        })
      }

      // One sentence for the whole pause. Naming the days would be noise: a
      // pause always covers every day the product is on.
      // A temporary pause is a Line Freeze now (DF-350): the line stays active
      // and a freeze says which deliveries it skips. One sentence per product,
      // like the old end-and-resume pause, across every day it covers. Its dates
      // are the deliveries actually missed on each day (the freeze itself ends
      // the day before the product resumes, which is rarely a delivery day).
      const frozen: { id: string, day: number, firstMissed: Date, lastMissed: Date }[] = []
      for (const [day, lines] of row.allLinesByDay) {
        for (const line of lines) {
          for (const freeze of freezes.filter(f => f.subscription_line_id === line.id)) {
            const firstMissed = onOrAfterWeekday(toStartOfDay(freeze.effective_from), day)
            const lastMissed = onOrBeforeWeekday(toStartOfDay(freeze.effective_to), day)
            if (firstMissed <= lastMissed) frozen.push({ id: freeze.freeze_id, day, firstMissed, lastMissed })
          }
        }
      }

      if (frozen.length > 0) {
        const from = frozen.reduce((a, b) => (b.firstMissed < a ? b.firstMissed : a), frozen[0].firstMissed)
        const to = frozen.reduce((a, b) => (b.lastMissed > a ? b.lastMissed : a), frozen[0].lastMissed)

        add({
          productId: row.productId,
          day: frozen[0].day,
          kind: 'freeze',
          lineId: row.linesByDay.get(frozen[0].day)?.id ?? '',
          freezeIds: frozen.map(f => f.id),
          text: from.getTime() === to.getTime()
            ? `Paused on ${formatFullDate(from)}`
            : `Paused from ${formatFullDate(from)} to ${formatFullDate(to)}`,
        })
      }

      if (pauses.length > 0) {
        const from = pauses.reduce((a, b) => (b.firstMissed < a ? b.firstMissed : a), pauses[0].firstMissed)
        const to = pauses.reduce((a, b) => (b.lastMissed > a ? b.lastMissed : a), pauses[0].lastMissed)

        add({
          productId: row.productId,
          day: pauses[0].day,
          kind: 'pause',
          lineId: pauses[0].ending,
          pausedLines: pauses.map(p => ({ lineId: p.ending, resumeLineId: p.resuming })),
          text: from.getTime() === to.getTime()
            ? `Paused on ${formatFullDate(from)}`
            : `Paused from ${formatFullDate(from)} to ${formatFullDate(to)}`,
        })
      }
    }
    return map
  }
